<script lang="ts" setup>
    import { onMounted, ref, computed } from 'vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import TextInput from '../../components/TextInput.vue';
    import SectionPanel from "../../components/SectionPanel.vue";
    import SearchDocumentFolder from '../../components/Search/SearchDocumentFolder.vue'
    import SearchLoinc from '../../components/Search/SearchLoinc.vue'
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue'
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue'
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import MessageSection from '../../components/Messages/MessageSection.vue'
    import { ICol, ValueStrDelegate, IRowSelection } from "../../lib/ChoiceTableHelpers";
    import { MenuNameType, PrivilegeType, AppScreens } from "../../Enums/ChoiceEnums";
    import { MessageSectionType, DisplayStyle, ButtonDisplaySize, TableRowType } from "../../Enums/UiEnums";
    import { IDocumentTypeItem, IDocumentTypeResult, IDocumentTypeFontItem, IDocumentTypeFontResult, IDocumentTypeColorItem, IDocumentTypeColorResult, IDocumentTypeSave } from '../../lib/Models/Document/DocumentTypeModel';
    import { ISearchDocumentFolderItem, ISearchDocumentFolderKey, ISearchLoincItem, ISearchLoincKey } from '../../lib/Models/SearchModel';
    import { ModalWindowOpen } from '../../lib/ChoiceUserConfiguration';
    import { documentType } from '../../lib/ChoiceApi';
    import { ActionButtonStyle } from "../../lib/Types/Types";

    import '../../../css/choice.css';

    const DEFAULT_VALUE = 0;
    const DEFAULT_TEXT = '[Default]';
    const tableData = ref<IDocumentTypeItem[]>([]);
    const tableDataCopy = ref<IDocumentTypeItem[]>([]);
    const windowHeight = ref<number>();
    const saveError = ref<boolean>(false);
    const saveErrorDescription = ref<string>("");
    const documentEditTitle = ref<string>("Document Type New");
    const intervalId = ref<number>(0);
    const searchDescription = ref<string>("");
    const localRowData = ref<IDocumentTypeItem & IRowSelection>();
    const save = ref<IDocumentTypeItem & IRowSelection>({document_type_id: DEFAULT_VALUE, description: "", enabled: true, core: false});
    const defaultFolderKey = ref<ISearchDocumentFolderKey>();
    const defaultLoincKey = ref<ISearchLoincKey>();
    const documentTypeActive = ref<HTMLInputElement>();
    const DocumentTypeDescription = ref<HTMLInputElement>();
    const documentTypeActiveSearch = ref<HTMLInputElement>();

    const FormatMenuPath: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
             return (row?.menu_path ?? "") + (null == row?.menu_id ? "" : ` (${row?.menu_id})`);
        }
    }
        
    const tableColumns = ref<ICol[]>([
        { Name: 'Icon', DataField: 'image_font_style' },
        { Name: 'Description', DataField: 'description' },
        { Name: 'LOINC', DataField: 'loinc_num' },
        { Name: 'Locked', DataField: 'core' },
        { Name: 'Save Location (Folder ID)', DataField: 'menu_path', FormatValue: FormatMenuPath },
        { Name: 'Active', DataField: 'enabled' },
    ]);

    const props = defineProps<{
        documentTypeResults: IDocumentTypeResult,
        fontResults: IDocumentTypeFontResult,
        colorResults: IDocumentTypeColorResult,
        privilege: PrivilegeType
    }>();

    onMounted(async () => {
        tableDataCopy.value = props.documentTypeResults.documentTypeList;
        tableData.value = props.documentTypeResults.documentTypeList;
        Resize();
        NewDocumentType();
        window.addEventListener('resize', () => {
            Resize();
        })
    });

    const GetFontStyleSelectedItem = computed<string>(() => {
        if (DEFAULT_VALUE == save.value.image_font_code || null == save.value.image_font_code) 
            return DEFAULT_TEXT;
        return `<span class="${save.value.image_font_style}"></span>`;
    });
    const GetColorSelectedItem = computed<string>(() => {
        if (DEFAULT_VALUE == save.value.color_id || null == save.value.color_id)
            return DEFAULT_TEXT;
        return `<span style="background-color: ${save.value.color_value};">&nbsp;&nbsp;</span>&nbsp;<span>${save.value.color_tooltip}</span>`;
    });
    const writePrivilege = computed<boolean>(() => props.privilege >= PrivilegeType.ReadUpdateWrite ? true : false);
    const updatePrivilege = computed<boolean>(() => props.privilege >= PrivilegeType.ReadUpdate ? true : false);
    const savePrivilege = computed<boolean>(() => updatePrivilege.value && !save.value.core);
    const activePrivilege = computed<boolean>(() => updatePrivilege.value );
    const auditEnabled = computed<boolean>(() => save.value.document_type_id > 0);

    function Resize() {
        windowHeight.value = window.innerHeight - 70 -  210;
    }

    async function NewDocumentType() {
        documentEditTitle.value = "Document Type New";
        save.value = {document_type_id: DEFAULT_VALUE, description: "", enabled: true, core: false};
        defaultFolderKey.value = {
            menuId: undefined
        }
        defaultLoincKey.value = {
            loincCode: undefined
        }
        setTimeout(() => { 
            DocumentTypeDescription.value?.focus();
        }, 100);
        
    }
    function GetCheckMark(enabled: boolean) {
        return enabled ? "fa-solid fa-circle-check text-secondary": "";
    }
    function GetLock(enabled: boolean) {
        return enabled ? "fa-solid fa-lock text-secondary": "";
    }
    function GetFontClass(fontStyle?: string, fontColor?: string) {
        if (!fontStyle)
            return "";
        return (null == fontColor ? "text-primary " : "") + `grid-icon ${fontStyle}`;
    }
    function GetFontStyle(fontColor?: string) {
        return (null == fontColor ? "" : `color:${fontColor}`)
    }
    function GetFontStyleDropdownItem(fontItem: IDocumentTypeFontItem) {
        if (DEFAULT_VALUE == fontItem.image_font_code)
            return DEFAULT_TEXT;
        return `<span class="${fontItem.image_font_style}"></span>`;
    }
    function GetColorDropdownItem(colorItem: IDocumentTypeColorItem) {
        if (DEFAULT_VALUE == colorItem.color_id)
            return DEFAULT_TEXT;
        return `<span style="background-color: ${colorItem.color_value};">&nbsp;&nbsp;</span>&nbsp;<span>${colorItem.tooltip}</span>`;
    }
    function documentTypeClick(rowData : IRowSelection, _rowType: TableRowType, _evt: MouseEvent) {
        save.value = {document_type_id: DEFAULT_VALUE, description: "", enabled: true, core: false};
        defaultFolderKey.value = {
            menuId: undefined
        }
        defaultLoincKey.value = {
            loincCode: undefined
        }
        localRowData.value = (rowData as IDocumentTypeItem & IRowSelection);
        documentEditTitle.value = "Document Type Edit";
        save.value = JSON.parse(JSON.stringify(localRowData.value));

        setTimeout(() => { 
            defaultFolderKey.value = {
                menuId: localRowData.value?.menu_id,
                display: localRowData.value?.menu_path
            }
            defaultLoincKey.value = {
                loincCode: localRowData.value?.loinc_num,
                display: GetLoincDisplay(localRowData.value?.loinc_num, localRowData.value?.loinc_description)
            }
        }, 200);
    }
    function GetLoincDisplay(loinc_num?: string, loinc_description?: string) {
        if (loinc_num && loinc_description)
            return `${loinc_num} ${loinc_description}`
        else if (loinc_num)
            return loinc_num;
        else 
            return "";
    }
    function OnChangeFolder(folder: ISearchDocumentFolderItem, menuNameType: MenuNameType) {
        if (folder) {
            save.value.menu_id = folder.folder_menu_id;
            save.value.menu_path = folder.path;
        }
    }
    function onChangeLoinc(result: ISearchLoincItem) {
        if (result) {
            save.value.loinc_num = result.loincCode;
            save.value.loinc_description = result.description;
        }
    }
    function OnClickFontDropdown(data: IDocumentTypeFontItem) {
        save.value.image_font_code = data.image_font_code;
        save.value.image_font_style = data.image_font_style;
    }
    function OnClickColorDropdown(data: IDocumentTypeColorItem) {
        save.value.color_id = data.color_id;
        save.value.color_value = data.color_value;
        save.value.color_tooltip = data.tooltip;
    }
    function OnEnabled() {
        if (!documentTypeActive.value)
            return;
        save.value.enabled = documentTypeActive.value?.checked;
    }
    function OnSearchKeypress() {
        if (intervalId.value != 0)
            window.clearInterval(intervalId.value);
        intervalId.value = window.setInterval(() => { 
            window.clearInterval(intervalId.value);
            DisplayFilterResults();
        }, 1000);
    }

    function OnActiveSearch() {
        DisplayFilterResults();
    }

    function DisplayFilterResults() {
        tableData.value = [...tableDataCopy.value].filter(FilterSearchItem);
    }
    function FilterSearchItem(item: IDocumentTypeItem) {
        return ((!documentTypeActiveSearch.value?.checked) || (documentTypeActiveSearch.value?.checked && item.enabled)) && 
            (item.description.toLowerCase().indexOf(searchDescription.value.toLowerCase()) >= 0 ||
            (item.loinc_num ?? "").toLowerCase().indexOf(searchDescription.value.toLowerCase()) >= 0 ||
            (item.loinc_description ?? "").toLowerCase().indexOf(searchDescription.value.toLowerCase()) >= 0 ||
            (item.menu_path ?? "").toLowerCase().indexOf(searchDescription.value.toLowerCase()) >= 0)
    }
    async function SaveDocumentType() {
        if (null == save.value.description || "" == save.value.description) {
            saveError.value = true;
            saveErrorDescription.value = "Description is a required fied."
            return;
        }
        if (null != save.value.menu_id) {
            const pathList = tableDataCopy.value.filter(o => o.document_type_id != save.value.document_type_id && o.menu_id == save.value.menu_id)
            if (pathList.length > 0) {
                saveErrorDescription.value = "Save Location has already been assigned to a Document Type."
                saveError.value = true;
                return;
            }
        }
        saveErrorDescription.value = "";
        saveError.value = false;
        const post_data = <IDocumentTypeSave> {
            document_type_id: save.value.document_type_id,
            description: save.value.description,
            enabled: save.value.enabled,
            image_font_code: save.value.image_font_code,
            loinc_num: save.value.loinc_num,
            menu_id: save.value.menu_id,
            color_id: save.value.color_id
        };

        const respSave = await documentType.save(post_data);
        if (respSave) {
            let index = tableData.value.findIndex(o => o.document_type_id == save.value.document_type_id);
            if (index < 0) {
                index = tableData.value.length; 
                save.value.document_type_id = respSave.document_type_id;               
            } 
            tableData.value[index] = JSON.parse(JSON.stringify(save.value));

            let indexCopy = tableDataCopy.value.findIndex(o => o.document_type_id == save.value.document_type_id);
            if (indexCopy < 0) {
                indexCopy = tableDataCopy.value.length; 
                save.value.document_type_id = respSave.document_type_id;               
            } 
            tableDataCopy.value[indexCopy] = JSON.parse(JSON.stringify(save.value));
        }
    }

    async function OpenAuditWindow() {
        ModalWindowOpen({screenID: AppScreens.Logging, urlParmData: {ScreenName: "DocumentTypeSetup", LogType: "s", StatementId: save.value.document_type_id.toString()}, title: "Payment Batch Audit"}); 
    }

</script>

<style scoped>
    .fixedColumn1 {
        width: 400px;
    }
    .fixedColumn2 {
        width: 400px;
    }
    .fixedColumn3 {
        width: 200px;
    }
</style>

<template>
    <SectionPanel>
        <div class="text-muted">{{documentEditTitle}}</div>
        <div class="row mt-2">
            <div class="fixedColumn1 label-isalus-small">
                Description
            </div>
            <div class="fixedColumn2 label-isalus-small">
                Save Location
            </div>
            <div class="fixedColumn3 label-isalus-small">
                Icon
            </div>
        </div>
        <div class="row">
            <div class="fixedColumn1">
                <TextInput 
                    v-model:text-value="save.description" 
                    ref="DocumentTypeDescription"
                    :maxLength="200"
                    placeholder="Please enter a description"
                    :disabled="!savePrivilege"
                    :cssFramework = "'vanilla'" />
            </div>
            <div class="fixedColumn2">
                <SearchDocumentFolder 
                    :menuNameId="MenuNameType.Patient"
                    :addToBaseClass="'label-isalus-small'" 
                    label=""
                    :maxHeight="200"
                    :disabled="!savePrivilege"
                    placeholder="Search Save Location" 
                    :defaultSearchKey="defaultFolderKey" 
                    @onChange="(val) => OnChangeFolder(val, MenuNameType.Patient)">
                </SearchDocumentFolder>
            </div>
            <div class="fixedColumn3">
                <DropDownSplitButton :disabled="!savePrivilege" :baseClass="'btn btn-primary label-isalus-small'">
                    <template v-slot:button><div v-html="GetFontStyleSelectedItem"></div></template>
                    <DropDownButtonItem v-for="fontItem in props.fontResults.fontList" v-bind:data="fontItem" :baseClass="'dropdown-item'" v-on:click="OnClickFontDropdown">
                        <div v-html="GetFontStyleDropdownItem(fontItem)"></div>
                    </DropDownButtonItem>
                </DropDownSplitButton>
            </div>
            <div class="row mt-2">
                <div class="fixedColumn1 label-isalus-small">
                    Loinc Code
                </div>
                <div class="fixedColumn2 label-isalus-small">
                </div>
                <div class="fixedColumn3 label-isalus-small">
                    Color
                </div>
            </div>
            <div class="fixedColumn1">
                <SearchLoinc 
                    :addToBaseClass="'label-isalus-small'" 
                    label=""
                    :maxHeight="200"
                    :disabled="!savePrivilege"
                    placeholder="Search LOINC" 
                    :defaultSearchKey="defaultLoincKey" 
                    @onChange="onChangeLoinc">
                </SearchLoinc>
            </div>
            <div class="fixedColumn2 mt-1">
                <input class="form-check-input me-1" 
                    ref="documentTypeActive" 
                    type="checkbox" 
                    :disabled="!activePrivilege" 
                    :checked="save.enabled" 
                    title="Is Document Type Active" 
                    @input="OnEnabled()">
                    <label class="label-isalus-small">Active</label>
            </div>
            <div class="fixedColumn3">
                <DropDownSplitButton :disabled="save.core" :baseClass="'btn btn-primary label-isalus-small'">
                    <template v-slot:button><div v-html="GetColorSelectedItem"></div></template>
                    <DropDownButtonItem v-for="colorItem in props.colorResults.colorList" v-bind:data="colorItem" :baseClass="'dropdown-item label-isalus-small'" v-on:click="OnClickColorDropdown">
                        <div v-html="GetColorDropdownItem(colorItem)"></div>
                    </DropDownButtonItem>
                </DropDownSplitButton>
            </div>
        </div>
        <div class="row mt-2">
            <div class="col-auto">
                <ActionButtonV2 
                    :action="ActionButtonStyle.New" 
                    text="New"
                    :size="ButtonDisplaySize.Small" 
                    :disabled="!writePrivilege" 
                    :performAction="NewDocumentType" />
            </div>
            <div class="col-auto">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Save" 
                    text="Save"
                    :size="ButtonDisplaySize.Small"  
                    :disabled="!activePrivilege" 
                    :performAction="SaveDocumentType" />
            </div>
            <div class="col-auto">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Audit" 
                    text="Audit"
                    :size="ButtonDisplaySize.Small"
                    :disabled="!auditEnabled"  
                    :performAction="OpenAuditWindow" />
            </div>
            <div class="col mt-1">
                <MessageSection v-if="save.core" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Document Type is locked and can only be set to be active/inactive.
                </MessageSection>   
                <MessageSection v-if="saveError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    {{saveErrorDescription}}
                </MessageSection>   
            </div>
        </div>
    </SectionPanel>
    <div class="row">
        <div class="col">
            <TextInput 
                v-model:text-value="searchDescription" 
                :maxLength="200"
                placeholder="Search"
                :disabled="false"
                :cssFramework = "'vanilla'"
                @keypress:textValue="OnSearchKeypress" />
        </div> 
        <div class="col-2">
            <input class="form-check-input me-1" 
                ref="documentTypeActiveSearch"
                type="checkbox" 
                :disabled="false" 
                :checked="false" 
                title="Display only active document types" 
                @input="OnActiveSearch()">
                <label class="label-isalus-small">Active Only</label>                
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <p style="font-size: 0.75rem;">
                <ChoiceTable 
                    ref="documentTypeTable"
                    :columns="tableColumns" 
                    :data="tableData" 
                    :show-filters="false" 
                    :allowSorting="false"
                    :height="windowHeight"
                    :key-value-data-member="'Col3Val'"
                    @rowclick="documentTypeClick">Loading...
                    <template #cell="{col, row, value}">
                        <span v-if="'Icon' == col.Name" :class="GetFontClass(row?.image_font_style, row?.color_value)" :style="GetFontStyle(row?.color_value)"></span>
                        <span v-if="'Active' == col.Name" :class="GetCheckMark(row.enabled)"></span>
                        <span v-if="'Locked' == col.Name" :class="GetLock(row.core)"></span>
                    </template>
                </ChoiceTable>
            </p>
        </div>
    </div>
</template>

